import React, { useState, useEffect } from 'react'
import { HardDrive, Loader, AlertCircle, AlertTriangle } from 'lucide-react'
import { useCloudStorage } from '../../hooks/useCloudStorage'

/**
 * Cloud Storage Usage Component for Sidebar
 * Shows current cloud storage usage with warning when near full
 */
const CloudStorageUsage = () => {
  const [storageQuota, setStorageQuota] = useState(null)
  const [loadingQuota, setLoadingQuota] = useState(false)
  const [error, setError] = useState(null)
  
  const { isConnected, connectedProvider, getStorageQuota } = useCloudStorage()

  // Format bytes to human readable format
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  // Load storage quota
  useEffect(() => {
    const loadStorageQuota = async () => {
      if (!isConnected) {
        setStorageQuota(null)
        setError(null)
        return
      }

      setLoadingQuota(true)
      setError(null)
      
      try {
        console.log('🔍 CloudStorageUsage: Loading storage quota using CloudStorageContext')
        const result = await getStorageQuota()
        
        if (result.success && result.quota) {
          const quota = result.quota
          setStorageQuota({
            totalBytes: quota.totalBytes,
            usedBytes: quota.usedBytes,
            availableBytes: quota.availableBytes || (quota.totalBytes - quota.usedBytes),
            usagePercentage: quota.usedBytes && quota.totalBytes ? (quota.usedBytes / quota.totalBytes * 100) : 0,
            usedFormatted: formatBytes(quota.usedBytes),
            totalFormatted: formatBytes(quota.totalBytes),
            availableFormatted: formatBytes(quota.availableBytes || (quota.totalBytes - quota.usedBytes)),
            providerType: connectedProvider
          })
          console.log('✅ CloudStorageUsage: Storage quota loaded successfully')
        } else {
          console.error('❌ CloudStorageUsage: Failed to load storage quota:', result.error)
          setError(result.error || 'Storage info unavailable')
          setStorageQuota(null)
        }
      } catch (err) {
        console.error('❌ CloudStorageUsage: Error loading storage quota:', err)
        setError('Storage info unavailable')
        setStorageQuota(null)
      } finally {
        setLoadingQuota(false)
      }
    }

    loadStorageQuota()
    
    // Reload every 5 minutes
    const interval = setInterval(loadStorageQuota, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [isConnected])

  // Not connected state
  if (!isConnected) {
    return (
      <div className="flex items-center">
        <HardDrive className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        <div className="ml-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Cloud Storage</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Not connected</p>
        </div>
      </div>
    )
  }

  // Loading state
  if (loadingQuota) {
    return (
      <div className="flex items-center">
        <Loader className="h-5 w-5 animate-spin text-blue-500 dark:text-blue-400" />
        <div className="ml-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Cloud Storage</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Loading usage...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center">
        <AlertCircle className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
        <div className="ml-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Cloud Storage</p>
          <p className="text-xs text-yellow-600 dark:text-yellow-400">{error}</p>
        </div>
      </div>
    )
  }

  // No quota data
  if (!storageQuota) {
    return (
      <div className="flex items-center">
        <HardDrive className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        <div className="ml-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Cloud Storage</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Connected</p>
        </div>
      </div>
    )
  }

  const isNearFull = storageQuota.usagePercentage >= 95
  const usagePercentage = storageQuota.usagePercentage || 0

  return (
    <div className="space-y-2">
      {/* Header with icon and title */}
      <div className="flex items-center">
        <HardDrive className={`h-5 w-5 ${isNearFull ? 'text-red-500 dark:text-red-400' : 'text-blue-500 dark:text-blue-400'}`} />
        <div className="ml-3 flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Cloud Storage</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {storageQuota.usedFormatted || formatBytes(storageQuota.usedBytes)} of {storageQuota.totalFormatted || formatBytes(storageQuota.totalBytes)}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      {storageQuota.totalBytes && (
        <div className="w-full">
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all duration-300 ${
                isNearFull 
                  ? 'bg-red-500 dark:bg-red-400' 
                  : usagePercentage > 80 
                    ? 'bg-yellow-500 dark:bg-yellow-400'
                    : 'bg-blue-500 dark:bg-blue-400'
              }`}
              style={{ 
                width: `${Math.min(usagePercentage, 100)}%` 
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className={`text-xs ${
              isNearFull 
                ? 'text-red-600 dark:text-red-400' 
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {usagePercentage.toFixed(1)}% used
            </span>
          </div>
        </div>
      )}

      {/* Warning message when storage is nearly full */}
      {isNearFull && (
        <div className="flex items-start space-x-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <AlertTriangle className="h-4 w-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700 dark:text-red-300 leading-tight">
            Notes can't be created if storage becomes full
          </p>
        </div>
      )}
    </div>
  )
}

export default CloudStorageUsage
